import React, { useEffect, useState } from "react";
import { GameClient } from "../gameClient";
import { UIButtonSmall, UIButtonThin } from "../styles/ButtonStyle";
import { Modal } from "./Modal";
import { Debug } from "../../../shared/debug";
import { advanceTrainingLevel, getDontShowTutorial, getFrequentVolume, getMainVolume, getMusicVolume, getSfxVolume, getTrainingLevel, setDontShowTutorial, setFrequentVolume, setMainVolume, setMusicVolume, setSfxVolume, setTrainingLevel } from "../userSettings";
import { openSteam } from "../../../shared/utils";
import { TrainingLevel } from "../../../shared/playerAccount";
/** @jsx jsx */
/* @ts-ignore */
import { jsx, css } from '@emotion/react';

interface SettingsPanelProps {
  setShowMixer: (show: boolean) => void;
  isOpen: boolean;
  onClose: () => void;
  showCredits: (show: boolean) => void;
}

declare var gameClient: GameClient;

const checkboxStyle = css`
  margin-bottom: 1rem;

  input[type="checkbox"] {
    transform: scale(1.5);
    margin-right: 1rem;
  }
`;


export const SettingsPanel = (props: SettingsPanelProps) => {
  const [dontShowTutorial, setNoShowTutorial] = useState(false);
  const [isOpened, setOpened] = useState(props.isOpen);

  useEffect(() => {
    setNoShowTutorial(getDontShowTutorial());
  }, []);

  const updateFreqVolume = (value: number) => {
    gameClient.setFreqVolume(value);
    setFrequentVolume(value);
  }

  const updateVolume = (value: number) => {
    gameClient.setVolume(value);
    setMainVolume(value);
  }

  const updateMusicVolume = (value: number) => {
    gameClient.setMusicVolume(value);
    setMusicVolume(value);
  }

  const updateSFXVolume = (value: number) => {
    gameClient.setSFXVolume(value);
    setSfxVolume(value);
  }

  const handleDismissTutorial = (event: React.ChangeEvent<HTMLInputElement>) => {
    gameClient.setDontShowTutorial(event.target.checked);
    setDontShowTutorial(event.target.checked);
    setNoShowTutorial(event.target.checked);
  }

  const resetTutorials = () => {
    gameClient.resetTutorials();
  }

  const trainingComplete = getTrainingLevel() === TrainingLevel.Trained;

  const skipOrResetTraining = () => {
    if (trainingComplete) {
      setTrainingLevel(TrainingLevel.NewPlayer);
    }
    else {
      setTrainingLevel(TrainingLevel.Trained)
    }
    gameClient.setUIState();
  }

  const incrementTraining = () => {
    advanceTrainingLevel(gameClient);
    gameClient.setUIState();
  }

  let restart = () => {
    props.onClose();
    if (gameClient.getCurrentState() !== 'lobby') {
      gameClient.goToLobby(false);
      setOpened(false);
    }
  }

  return (
    <Modal onClose={props.onClose} isOpen={isOpened} height="fit-content" width="30vw" zIndex={13 /* above tutorial */}>
      <h2>Settings</h2>
      <br />
      <div>Main Volume</div>
      <VolumeSlider getValue={getMainVolume} onChange={updateVolume} />
      <div>Music Volume</div>
      <VolumeSlider getValue={getMusicVolume} onChange={updateMusicVolume} />
      <div>Sound Effects Volume</div>
      <VolumeSlider getValue={getSfxVolume} onChange={updateSFXVolume} />
      <div>Frequent Sounds Volume (hover sounds, chain sounds, etc)</div>
      <VolumeSlider getValue={getFrequentVolume} onChange={updateFreqVolume} />
      <br />
      {
        Debug.EnableAudioMixer && <UIButtonThin onClick={() => { props.setShowMixer(true); setOpened(false); }}>Mixer</UIButtonThin>
      }
      <div>
        <div css={checkboxStyle}>
          <input type="checkbox" id="tutorial" name="tutorial" checked={dontShowTutorial} onChange={handleDismissTutorial} />
          <label htmlFor="tutorial">Disable Tutorial On Game Start</label>
        </div>
        {gameClient.isElectron && <UIButtonThin onClick={() => { gameClient.toggleFullscreen() }}>Toggle Fullscreen</UIButtonThin>}
        <UIButtonThin onClick={resetTutorials}>Reset Tutorials</UIButtonThin>
        <br />
        {process.env.NODE_ENV === 'development' && <div>Current Level: {getTrainingLevel()}</div>}
        {process.env.NODE_ENV === 'development' && <UIButtonSmall onClick={incrementTraining}>Increase Level</UIButtonSmall>}
        <UIButtonThin onClick={skipOrResetTraining}>{trainingComplete ? "Restart Training" : "Skip Training"}</UIButtonThin>
        <br />
        {
          gameClient.getCurrentState() !== 'lobby' &&
          <UIButtonThin onClick={restart}>Back to Start</UIButtonThin>
        }
        {
          process.env.is_demo &&
          <UIButtonThin onClick={openSteam}>Wishlist!</UIButtonThin>
        }
        <UIButtonThin
          onClick={() => {
            setOpened(false)
            props.onClose()
            props.showCredits(true)
          }}
        >
          Credits
        </UIButtonThin>
      </div>
    </Modal >
  )
}

interface VolumeSliderProps {
  onChange: (value: number) => void;
  getValue: () => number;
}

const VolumeSlider = ({ getValue, onChange }: VolumeSliderProps) => {
  const [volume, setVolume] = useState('50'); // Initial volume value (range: 0-100)

  const handleVolumeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = event.target.value;
    setVolume(newVolume);
    const volume = parseInt(newVolume) / 100;
    onChange(volume);
  };

  useEffect(() => {
    const volume = getValue();
    if (volume) {
      setVolume((volume * 100).toFixed(0).toString());
    }
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    }}>
      <input
        style={{ width: '90%' }}
        type="range"
        min="0"
        max="100"
        value={volume}
        onChange={handleVolumeChange}
      />
      <span>{volume}</span>
    </div>
  );
};
